import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { hasDesignationFormatInText } from 'components/WorkspaceEditor/features/drawingDesignation/utils/designationFormatValidator'
import type { DataNode } from 'components/WorkspaceEditor/types'
import { isManufacturerOrBODHeader } from 'components/WorkspaceEditor/utils'
import { useCallback, useMemo, useRef, useState } from 'react'
import { getConcatenatedText } from '../utils/textUtils'

/**
 * Custom hook that handles parsing and manipulating keyed text content.
 *
 * @param {Partial<TextContentElement>} contentItem - The content item data, potentially incomplete.
 * @param {boolean} [isContentTeamFromStudioPage] - Whether the current content is from SMM/OM with special permissions.
 * @param {DataNode} [node] - The parent node, used to check for MFR/BOD exclusions.
 * @returns An object containing parsed text and handlers for managing keyed content.
 */

export const useKeyedText = (
    contentItem: Partial<TextContentElement>,
    isContentTeamFromStudioPage?: boolean,
    node?: DataNode
) => {
    const text = useMemo(() => getConcatenatedText(contentItem), [contentItem])
    const hasColonInText = text.includes(':')

    // Check if this content has FieldRef elements (used for drawing designations)
    const hasFieldRefElement = useMemo(
        () =>
            contentItem.elements?.some((el) => el.type === 'FieldRef') ?? false,
        [contentItem.elements]
    )

    // Check if this is MFR/BOD base content (header node)
    // MFR/BOD headers contain colons but are NOT key-value pairs
    const isMfrBodBaseContent = node ? isManufacturerOrBODHeader(node) : false

    const isExcludedContent =
        isContentTeamFromStudioPage ||
        contentItem.changeType?.toUpperCase() === 'ADDED' || // exclude studio page content and user-added content
        isMfrBodBaseContent // exclude MFR/BOD base content (Bug 2568131 fix)
    const calculatedHasDesignationFormat =
        hasDesignationFormatInText(text) && !isExcludedContent
    const calculatedIsKeyedText = hasColonInText && !isExcludedContent

    // Once content is identified as keyed text, it should remain stable throughout the editing session
    const [isKeyedText] = useState(calculatedIsKeyedText)

    // This ensures DesignationField keeps showing even if designation is cleared, and handles
    const hasDesignationFormatRef = useRef(calculatedHasDesignationFormat)
    if (calculatedHasDesignationFormat && !hasDesignationFormatRef.current) {
        hasDesignationFormatRef.current = true
    }
    const hasDesignationFormat = hasDesignationFormatRef.current

    // Capture the initial keyed text and preserve it throughout the session
    const [initialKeyedText] = useState(() => {
        if (!calculatedIsKeyedText) return ': '
        const colonIndex = text.indexOf(':')
        if (colonIndex === -1) return ': '

        return text.substring(0, colonIndex + 1)
    })

    // This ensures drawing designations update after mutation while maintaining stability during editing
    const stableKeyedText = useMemo(() => {
        if (!isKeyedText) return ': '

        // If current text has a colon, extract the key from it (for dynamic updates like drawing designations)
        const colonIndex = text.indexOf(':')
        if (colonIndex !== -1) {
            return text.substring(0, colonIndex + 1)
        }

        // If current text doesn't have a colon, use the preserved initial key
        return initialKeyedText
    }, [text, isKeyedText, initialKeyedText])

    const { keyedText, keyedTextValue } = useMemo(() => {
        // Case 1: Traditional keyed text with colon
        if (isKeyedText) {
            const valuePart = text.includes(':')
                ? text.substring(stableKeyedText.length)
                : undefined
            return {
                keyedText: stableKeyedText,
                keyedTextValue: valuePart,
            }
        }

        // Case 2: Designation format without colon
        if (hasDesignationFormat) {
            return {
                keyedText: text + ':',
                keyedTextValue: undefined,
            }
        }

        // Case 3: No keyed text and no designation format
        return { keyedText: ': ', keyedTextValue: undefined }
    }, [text, isKeyedText, stableKeyedText, hasDesignationFormat])

    /**
     * Checks if key element already exists in the array to prevent duplication
     */
    const keyElementExists = useCallback(
        (elements: Partial<TextContentElement>[]): boolean => {
            return elements.some((element) =>
                element.text?.trim().startsWith(keyedText.trim())
            )
        },
        [keyedText]
    )

    /**
     * Generates unique ID for key element
     */
    const generateKeyElementId = useCallback(
        (elements: Partial<TextContentElement>[]): string => {
            const firstElement = elements[0]
            return firstElement?.id ? `${firstElement.id}_key` : 'generated_key'
        },
        []
    )

    /**
     * Prepends a new element containing only the key text (e.g., "Label: ") to the array,
     * resetting change tracking while preserving the original element's ID.
     */
    const prependKeyedTextElement = useCallback(
        (textElements: Partial<TextContentElement>[]) => {
            if (!isKeyedText || textElements.length === 0) return

            // Skip prepending for FieldRef-based keyed text
            if (hasFieldRefElement) return

            // Prevent duplication - exit early if key element already exists
            if (keyElementExists(textElements)) return

            const keyPreservingElement: Partial<TextContentElement> = {
                id: generateKeyElementId(textElements),
                text: keyedText,
                changeType: null,
                authoredUserId: '',
            }

            // prepend the keyedText element at the start of textElements
            textElements.unshift(keyPreservingElement)
        },
        [
            isKeyedText,
            keyedText,
            hasFieldRefElement,
            keyElementExists,
            generateKeyElementId,
        ]
    )

    return {
        keyedText,
        keyedTextValue,
        isKeyedText,
        hasDesignationFormat,
        prependKeyedTextElement,
    }
}
